import type { ReactNode } from 'react'
import { useState } from 'react'

import { Box, Button, Stack, TextField, Tooltip, Typography } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import { PackConvert, UpdatePackConversionRequest } from '@services/packConverts.types'
import { getTestId } from '@utils/helpers'
import { Statuses } from '@views/MarketBaskets/View/components/PurchasesTable/PurchasesTable.utils'
import { useBasketFilters } from '@views/MarketBaskets/View/MarketBasket.hooks'
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params'

type Props = {
	packConvert: PackConvert
	onUpdate: (request: UpdatePackConversionRequest) => void
	autoFocus?: boolean
}

const decimalOnly = (value: string) => /^\d*\.?\d*$/.test(value)

function getSavingsColor(isEdited: boolean, savingsPercentage: number) {
	if (isEdited) return 'text.secondary'
	if (savingsPercentage < 0) return 'error.main'
	return 'inherit'
}

type CellSx = SystemStyleObject<Theme>

const purchaseCellSx: CellSx = {
	bgcolor: 'grey.100',
	borderBottom: '1px solid',
	borderColor: 'divider',
}

export default function PackConvertItem({ packConvert, onUpdate, autoFocus = false }: Props) {
	const [, setActiveTask] = useQueryParam('active-task', StringParam)
	const [, setActionCenter] = useQueryParam('action-center', BooleanParam)
	const [, setPurchaseId] = useQueryParam('purchaseId', StringParam)

	const [, updateBasketFilters] = useBasketFilters()

	const {
		purchaseMpc,
		purchaseMfgProdDmid,
		purchaseManufacturer,
		purchaseDescription,
		purchasePackSize,
		purchaseUnitsPerPack,
		purchaseUnit,
		purchaseUnitPrice,
		candidateManufacturer,
		candidateMpc,
		candidateMfgProdDmid,
		candidateUnitPrice,
		candidateDescription,
		candidatePackSize,
		candidateUnitsPerPack,
		candidateUnit,
		savingsPercentage,
	} = packConvert

	const [purchaseValue, setPurchaseValue] = useState(purchaseUnitsPerPack.toString())
	const [candidateValue, setCandidateValue] = useState(candidateUnitsPerPack.toString())

	const handleUpdate = () => {
		onUpdate({
			purchaseMfgProdDmid,
			candidateMfgProdDmid,
			purchaseUnits: parseFloat(purchaseValue),
			switchCandidateUnits: parseFloat(candidateValue),
		})
	}

	const handleView = () => {
		setActiveTask(null)
		setActionCenter(false)
		setPurchaseId(null)
		updateBasketFilters({
			group: null,
			category: null,
			manufacturer: null,
			search: purchaseMpc,
			brandOwner: null,
			exclusion: null,
			packSize: null,
			matchType: null,
			sameManufacturerSwitch: null,
			qty: null,
			extendedPrice: null,
			status: Statuses.All,
			reviewType: null,
		})
	}

	const computedConversionFactor = Number(
		((parseFloat(candidateValue) || 0) / (parseFloat(purchaseValue) || 0)).toFixed(4)
	)
	const hasSavingsPercentage = Number.isFinite(savingsPercentage)
	const hasConversionFactor = Number.isFinite(computedConversionFactor)
	const purchaseUnitPriceLabel = purchaseUnitPrice ? `$${purchaseUnitPrice.toFixed(2)}` : '--'
	const candidateUnitPriceLabel = candidateUnitPrice ? `$${candidateUnitPrice.toFixed(2)}` : '--'
	const sameUnit = purchaseUnit === candidateUnit

	const isPackConversionEdited =
		purchaseValue !== purchaseUnitsPerPack.toString() ||
		candidateValue !== candidateUnitsPerPack.toString()

	const savingsColor = getSavingsColor(isPackConversionEdited, savingsPercentage)

	return (
		<Box
			data-testid={`pack-convert-item-${packConvert.id}`}
			sx={{
				borderTop: 2,
				borderTopColor: 'text.primary',
				border: 1,
				borderLeftColor: 'divider',
				borderRightColor: 'divider',
				bgcolor: 'background.paper',
				flexShrink: 0,
			}}
		>
			<Stack direction="row">
				<Stack flex={1}>
					{/* Label Column */}
					<Cell header />
					<Cell sx={purchaseCellSx}>
						<Typography variant="body2" fontWeight="bold" color="text.secondary">
							Purch
						</Typography>
					</Cell>
					<Cell>
						<Typography variant="body2" fontWeight="bold" color="text.secondary">
							Match
						</Typography>
					</Cell>
				</Stack>

				{/*  Manufacturer */}
				<Column
					label="MFR"
					purchaseValue={purchaseManufacturer ?? '--'}
					candidateValue={candidateManufacturer ?? '--'}
					flex={2}
					align="left"
				/>

				{/*  Description */}
				<Column
					label="Description"
					purchaseValue={purchaseDescription}
					candidateValue={candidateDescription}
					align="left"
					flex={5}
				/>

				{/*  MPC */}
				<Column
					label="MPC"
					align="left"
					purchaseValue={purchaseMpc ?? '--'}
					candidateValue={candidateMpc ?? '--'}
					purchaseTestId={`purchase-pack-${packConvert.id}-mpc`}
					candidateTestId={`candidate-pack-${packConvert.id}-mpc`}
					flex={2}
				/>

				{/* Pack Size */}
				<Column
					align="left"
					label="Pack Size"
					purchaseValue={purchasePackSize}
					candidateValue={candidatePackSize}
					flex={1}
				/>

				{/* Unit/Price */}
				<Stack flex={2} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
					<Cell header>
						<Typography variant="subtitle2" fontWeight="bold">
							Unit/Price
						</Typography>
					</Cell>
					<Cell sx={purchaseCellSx}>
						<Stack direction="row" spacing={0.5} alignItems="center">
							<Typography
								variant="body2"
								height="100%"
								fontWeight={sameUnit ? 'inherit' : 'bold'}
								color={sameUnit ? 'inherit' : 'error.main'}
							>
								{purchaseUnit}
							</Typography>
							<Typography variant="body2" height="100%" fontWeight={sameUnit ? 'inherit' : 'bold'}>
								{purchaseUnitPriceLabel}
							</Typography>
						</Stack>
					</Cell>
					<Cell>
						<Stack direction="row" spacing={0.5} alignItems="center">
							<Typography
								variant="body2"
								height="100%"
								fontWeight={sameUnit ? 'inherit' : 'bold'}
								color={sameUnit ? 'inherit' : 'error.main'}
							>
								{candidateUnit}
							</Typography>
							<Typography variant="body2" height="100%" fontWeight={sameUnit ? 'inherit' : 'bold'}>
								{candidateUnitPriceLabel}
							</Typography>
						</Stack>
					</Cell>
				</Stack>

				{/* Conversion */}
				<Stack flex={1} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
					<Cell header>
						<Typography variant="subtitle2" fontWeight="bold">
							Conversion
						</Typography>
					</Cell>
					<Cell fullHeight>
						<Typography variant="h6" height="100%" fontWeight="bold">
							{hasConversionFactor ? computedConversionFactor : '--'}
						</Typography>
					</Cell>
				</Stack>

				{/* Savings */}
				<Stack flex={1}>
					<Cell header>
						<Typography variant="subtitle2" fontWeight="bold">
							Savings
						</Typography>
					</Cell>
					<Cell fullHeight>
						<Tooltip
							title={isPackConversionEdited ? 'Correct savings % can not be calculated' : ''}
						>
							<Typography
								variant="h6"
								height="100%"
								color={savingsColor}
								fontWeight="bold"
								data-testid={`pack-convert-item-${packConvert.id}-savings`}
								sx={isPackConversionEdited ? { textDecoration: 'line-through' } : undefined}
							>
								{hasSavingsPercentage ? `${savingsPercentage.toFixed(0)}%` : '--%'}
							</Typography>
						</Tooltip>
					</Cell>
				</Stack>

				{/* Pack Editor */}
				<Stack flex={1}>
					<Cell header align="left">
						<Typography variant="subtitle2" fontWeight="bold">
							Unit/Pack
						</Typography>
					</Cell>
					<Cell>
						<PackEditor
							value={purchaseValue}
							onChange={setPurchaseValue}
							autoFocus={autoFocus}
							backgroundColor="grey.100"
							data-testid={`purchase-pack-${packConvert.id}`}
						/>
					</Cell>
					<Cell>
						<PackEditor
							value={candidateValue}
							onChange={setCandidateValue}
							data-testid={`candidate-pack-${packConvert.id}`}
						/>
					</Cell>
				</Stack>

				{/* Actions */}
				<Stack spacing={1} flex={1} height="100%">
					<Cell header />
					<Cell fullHeight>
						<Stack spacing={1}>
							<Button
								variant="contained"
								color="secondary"
								size="small"
								onClick={handleUpdate}
								data-testid={`pack-convert-item-${packConvert.id}-update-button`}
								sx={{ px: 2 }}
								disabled={!(Number(purchaseValue) > 0) || !(Number(candidateValue) > 0)}
							>
								Update
							</Button>
							<Button
								variant="text"
								color="info"
								size="small"
								onClick={handleView}
								sx={{ textDecoration: 'underline' }}
								data-testid={`pack-convert-item-${packConvert.id}-view-button`}
							>
								View
							</Button>
						</Stack>
					</Cell>
				</Stack>
			</Stack>
		</Box>
	)
}

function Column({
	label,
	purchaseValue,
	candidateValue,
	align = 'center',
	flex = 1,
	purchaseTestId = undefined,
	candidateTestId = undefined,
}: {
	label: string
	purchaseValue: ReactNode
	candidateValue: ReactNode
	align?: 'left' | 'center'
	flex?: number
	purchaseTestId?: string
	candidateTestId?: string
}) {
	return (
		<Stack sx={{ flex, minWidth: 0 }}>
			<Cell header align={align}>
				<Typography variant="body2" fontWeight="bold">
					{label}
				</Typography>
			</Cell>
			<Cell align={align} data-testid={purchaseTestId} sx={purchaseCellSx}>
				{purchaseValue}
			</Cell>
			<Cell align={align} data-testid={candidateTestId}>
				{candidateValue}
			</Cell>
		</Stack>
	)
}

function Cell({
	children = null,
	header = false,
	align = 'center',
	fullHeight = false,
	sx = {},
	...rest
}: {
	children?: ReactNode
	header?: boolean
	align?: 'left' | 'center'
	fullHeight?: boolean
	sx?: CellSx
}) {
	const cellSx: CellSx = {
		height: fullHeight ? 72 : 36,
		px: 2,
		py: 2.5,
		display: 'flex',
		alignItems: 'center',
		justifyContent: align === 'left' ? 'flex-start' : 'center',
		textAlign: align,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		borderBottom: 0,
		borderColor: 'divider',
		bgcolor: header ? 'grey.300' : 'background.paper',
		'& .MuiTypography-root': {
			minWidth: 0,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		...sx,
	}

	return (
		<Box {...rest} sx={cellSx}>
			{children}
		</Box>
	)
}

function PackEditor({
	value,
	onChange,
	autoFocus = false,
	backgroundColor = 'background.paper',
	...rest
}: {
	value: string
	onChange: (value: string) => void
	autoFocus?: boolean
	backgroundColor?: string
}) {
	const testId = getTestId(rest, 'packEditor')

	return (
		<Stack direction="column" spacing={0.5} sx={{ width: '100%', alignItems: 'center' }} {...rest}>
			<TextField
				value={value}
				size="small"
				autoFocus={autoFocus}
				sx={{ width: '6rem', m: 0, bgcolor: backgroundColor }}
				slotProps={{
					htmlInput: {
						'data-testid': testId,
						sx: { textAlign: 'left' },
					},
				}}
				onChange={(e) => {
					if (decimalOnly(e.target.value)) onChange(e.target.value)
				}}
			/>
		</Stack>
	)
}
